import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { formatInteger } from '@monetize/utils/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import MButton from '../../MButton';
import MCheckbox from '../../MCheckbox';
import MCustomIconButton from '../../MCustomIconButton';
import { MSearchInput } from '../../MSearchInput';
import MText from '../../MText';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { MBox, MFlex } from '../../chakra';
import { DataTableFilterRef, FilterProps } from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

// Stable value to avoid re-renders
const EMPTY_ARRAY: Array<{ label: string; value: string | number | boolean }> =
  [];

/**
 * Filter function for the Set filter
 * Set filter is a set of values shown to the user, similar to excel
 * This filter is only available if all filter values are known in advance
 * * 100% browser side filtering - available for all text fields
 * * Server side filtering - available for enums where we know the values in advance
 *   - *NOT YET IMPLEMENTED FOR SERVER
 *   - *Should be refactored to use filter facets
 *
 * @param row
 * @param columnId
 * @param filterValue
 * @param addMeta
 * @returns
 */
/**
 * "Select All" toggles only the options the search currently shows, so the
 * count must follow the search. "shown" keeps it from reading as a
 * selected-count when the box is indeterminate.
 */
export const getSelectAllLabel = (
  visibleCount: number,
  totalCount: number,
): string => {
  if (visibleCount === totalCount) {
    return `Select All (${formatInteger(totalCount)})`;
  }
  return `Select All (${formatInteger(visibleCount)} of ${formatInteger(
    totalCount,
  )} shown)`;
};

export const DataTableHeaderSetFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table, clientControlled }, ref) => {
  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (columnKey === column.id && value instanceof Set) {
        setSelectedFilters(value);
        setAppliedFilters(value);
        table.options.meta?.selectedFilters.set(key, value);
        // Always record the filter so the active-filter chips can render; only
        // server-controlled tables act on it to refetch.
        table.options.meta?.onFilterUpdate?.(key, value);
        if (clientControlled) {
          column.setFilterValue(value);
        }
      }
    },
  }));

  const key = getColumnFilterKey(column);
  const filterValues =
    table.options.meta?.filterValues?.get(key) || EMPTY_ARRAY;

  // Used to detect if new values are available when the data changes. Seeded
  // lazily from the first render's values: an empty `Set` is truthy, so the
  // previous `if (!ref.current)` guard never ran and left this empty, which
  // made the first data settle look like every value had just appeared.
  const prevFilterValues = useRef<Set<string | number | boolean> | null>(null);
  if (prevFilterValues.current === null) {
    prevFilterValues.current = new Set(filterValues.map(({ value }) => value));
  }

  // Held in state, not a ref: the popover's presence gate mounts the content a
  // render after `open` flips, so a ref would fill in without re-rendering this
  // component and the virtualizer would keep its empty, unmeasured row range -
  // leaving the list blank on first open.
  const [scrollableContainer, setScrollableContainer] =
    useState<HTMLDivElement | null>(null);
  const { open, onOpen, onClose } = useDisclosure();

  // Calculate initial filter value once and reuse for both states
  const getInitialFilterValue = (): Set<string | number | boolean> => {
    // For client-controlled, select all by default
    if (clientControlled) {
      return new Set<string | number | boolean>(
        filterValues?.map(({ value }) => value),
      );
    }

    // For server-controlled, check if there are initial filters from meta
    const existingFilter = table.options.meta?.selectedFilters?.get(key);
    if (existingFilter instanceof Set) {
      return existingFilter as Set<string | number | boolean>;
    }
    if (Array.isArray(existingFilter)) {
      return new Set(existingFilter);
    }

    return new Set<string | number | boolean>();
  };

  // Store the applied filters separately from the working selection
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilterValue);
  const [selectedFilters, setSelectedFilters] = useState(getInitialFilterValue);

  const [visibleFilters, setVisibleFilters] = useState(() => filterValues);

  const [searchTerm, setSearchTerm] = useState('');
  const [allSelected, setAllSelected] = useState(
    () => selectedFilters.size === filterValues.length,
  );
  const [someSelected, setSomeSelected] = useState(
    () => selectedFilters.size === filterValues.length,
  );

  useNonInitialEffect(() => {
    // Only update meta and column filter when filters are actually applied (not during selection changes)
    if (!open) {
      table.options.meta?.selectedFilters.set(key, selectedFilters);
      // Only set filter value for client-controlled tables here
      // Server-controlled tables handle this in a separate effect to avoid double-calling
      if (clientControlled) {
        column.setFilterValue(selectedFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedFilters]);

  useNonInitialEffect(() => {
    // if every item in the list is selected, set all selected to true regardless of what is visible
    if (selectedFilters.size === filterValues.length) {
      setAllSelected(true);
    } else if (visibleFilters.length > selectedFilters.size) {
      // if more visible than are selected, it is not possible for all to be selected
      setAllSelected(false);
    } else {
      // See if every visible filter is selected
      setAllSelected(
        visibleFilters.every(({ value }) => selectedFilters.has(value)),
      );
    }
  }, [filterValues, selectedFilters, visibleFilters]);

  /**
   * Handle case where filters are applied but the data in the table changes
   * Some selections may no longer be valid
   * Other selections may need to be added to the list and auto-selected
   *
   * NOTE: This only runs for client-controlled tables. Server-controlled tables
   * preserve their initial filters from meta.selectedFilters without interference.
   */
  useNonInitialEffect(() => {
    if (selectedFilters.size === 0 || !clientControlled) {
      return;
    }

    const allValues = new Set(filterValues.map(({ value }) => value));
    // Auto-select any new values that just got added to list
    const newValues = filterValues
      .filter(({ value }) => !prevFilterValues.current?.has(value))
      .map(({ value }) => value);
    // Advance the ref here, once. Doing it inside the updater below made the
    // updater impure: it is handed to two setState calls, and the second saw
    // the already-advanced ref, found no new values, and kept the stale
    // selection - so rows added to the table stayed filtered out.
    prevFilterValues.current = new Set(allValues);

    const updateFilters = (prevValue: Set<string | number | boolean>) => {
      // if all selected values are in list and there are no new values, keep prior selection
      if (
        newValues.length === 0 &&
        Array.from(prevValue).every((value) => allValues.has(value))
      ) {
        return prevValue;
      }
      const newValue = new Set([...prevValue, ...newValues]);
      // remove any selected values that are no longer in the table
      newValue.forEach((value) => {
        if (!allValues.has(value)) {
          newValue.delete(value);
        }
      });
      // Select all if no values are selected
      if (newValue.size === 0) {
        return new Set<string | number | boolean>(allValues);
      }
      return newValue;
    };

    setSelectedFilters(updateFilters);
    // Only update appliedFilters if the modal is closed (meaning filters are already applied)
    if (!open) {
      setAppliedFilters(updateFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  useNonInitialEffect(() => {
    // if filters change while modal is closed, update column filter
    // to ensure visible rows are modified (we potentially auto-select some values when data changes)
    // e.x. if data is reloaded and new values are added
    // Only for server-controlled tables - client-controlled is handled in the effect above
    if (!open && !clientControlled) {
      column.setFilterValue(selectedFilters);
    }
  }, [selectedFilters, open, column, clientControlled]);

  useEffect(() => {
    if (allSelected) {
      setSomeSelected(false);
    } else {
      // see if any, but not all visible items are selected
      setSomeSelected(
        visibleFilters.some(({ value }) => selectedFilters.has(value)),
      );
    }
  }, [allSelected, filterValues, selectedFilters, visibleFilters]);

  useNonInitialEffect(() => {
    if (filterValues && searchTerm) {
      const searchTermLowercase = searchTerm.toLowerCase();
      setVisibleFilters(
        filterValues.filter(({ label }) =>
          label.toLowerCase().includes(searchTermLowercase),
        ),
      );
    } else {
      setVisibleFilters(filterValues);
    }
  }, [searchTerm, filterValues]);

  const handleFilterChange = (value: any, checked: boolean) => {
    if (checked) {
      setSelectedFilters((prev) => {
        const newSet = new Set(prev);
        newSet.add(value);
        return newSet;
      });
    } else {
      setSelectedFilters((prev) => {
        const newSet = new Set(prev);
        newSet.delete(value);
        return newSet;
      });
    }
  };

  const handleToggleSelectAll = () => {
    if (!allSelected) {
      setSelectedFilters(new Set(visibleFilters.map(({ value }) => value)));
    } else {
      setSelectedFilters(new Set());
    }
  };

  const handleResetFilters = () => {
    const filters = clientControlled
      ? new Set(filterValues.map(({ value }) => value))
      : new Set([]);

    // Update states first
    setSelectedFilters(filters);
    setAppliedFilters(filters);

    // Update table metadata
    table.options.meta?.selectedFilters.set(key, filters);

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(
        key,
        new Set<string | number | boolean>(),
      );
    }
    // Note: column.setFilterValue is called by the effects when selectedFilters changes
    // Close the popover
    onClose();
    setSearchTerm('');
  };

  const handleApplyFilters = () => {
    // Update the applied filters state
    setAppliedFilters(new Set(selectedFilters));

    if (table.options.meta?.onFilterUpdate) {
      const filterValueForTags =
        clientControlled && selectedFilters.size === filterValues.length
          ? new Set<string | number | boolean>()
          : selectedFilters;
      table.options.meta.onFilterUpdate(key, filterValueForTags);
    }
    // Note: column.setFilterValue is called by the effects when selectedFilters changes
    onClose();
  };

  const handleClose = () => {
    // Revert to the last applied filters when closing without applying
    setSelectedFilters(new Set(appliedFilters));
    onClose();
    setSearchTerm('');
  };

  const filtersApplied =
    appliedFilters.size > 0 && appliedFilters.size < filterValues.length;

  const rowVirtualizer = useVirtualizer({
    count: visibleFilters.length,
    getScrollElement: () => scrollableContainer,
    estimateSize: () => 21,
    overscan: 15,
  });

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter</Popover.Header>
              <Popover.Body minW="304px">
                {filterValues && filterValues?.length > 0 && (
                  <>
                    {/* Margin on the wrapper: on the input it would stretch
                        the input group and pull the icon off-center. */}
                    <MBox mb={2}>
                      <MSearchInput
                        value={searchTerm}
                        omitCount
                        onChange={setSearchTerm}
                      />
                    </MBox>
                    <MBox mb={1}>
                      <MCheckbox
                        isChecked={allSelected}
                        isIndeterminate={someSelected}
                        onChange={handleToggleSelectAll}
                      >
                        <MText fontWeight={500}>
                          {getSelectAllLabel(
                            visibleFilters.length,
                            filterValues.length,
                          )}
                        </MText>
                      </MCheckbox>
                    </MBox>
                    {/* The ref goes on the box that scrolls, not the
                        full-height list inside it: measured there, the
                        virtualizer saw the whole list as its viewport and
                        mounted every row - thousands of checkboxes. */}
                    <MBox
                      ref={setScrollableContainer}
                      maxH="250px"
                      overflowY="auto"
                      width="100%"
                    >
                      <MFlex
                        height={`${rowVirtualizer.getTotalSize()}px`}
                        position="relative"
                        flexDirection="column"
                      >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                          const { label, value } =
                            visibleFilters[virtualRow.index];
                          return (
                            <MBox
                              key={virtualRow.key}
                              position="absolute"
                              top="0"
                              left="0"
                              width="100%"
                              height={`${virtualRow.size}px`}
                              transform={`translateY(${virtualRow.start}px)`}
                            >
                              <MCheckbox
                                isChecked={selectedFilters.has(value)}
                                onChange={(e) => {
                                  handleFilterChange(value, e.target.checked);
                                }}
                              >
                                <MText fontWeight={400} mb={1}>
                                  {label}
                                </MText>
                              </MCheckbox>
                            </MBox>
                          );
                        })}
                      </MFlex>
                    </MBox>
                  </>
                )}
              </Popover.Body>
              <Popover.Footer>
                <MFlex justifyContent="space-between">
                  <MButton
                    variant="tertiary"
                    size="sm"
                    onClick={handleResetFilters}
                    isDisabled={selectedFilters.size === 0}
                  >
                    Reset
                  </MButton>
                  <MButton size="sm" onClick={handleApplyFilters}>
                    Apply
                  </MButton>
                </MFlex>
              </Popover.Footer>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
